import type { StateCreator } from 'zustand';
import type { AppStore } from './types';
import type { ExpeditionRun } from '@/domain/expedition/types';
import type { EnemyRuntime } from '@/domain/combat/types';
import type { MutationDef } from '@/domain/mutation/types';
import {
  makeCombatActionClock,
  type CombatActionClock,
} from '@/domain/combat/action-timing';
import type { CombatProjectile } from '@/domain/combat/projectile';

export interface CombatSliceState {
  activeRun: ExpeditionRun | null;
  totalRuns: number;
  inExpedition: boolean;
  /** HP/maxHp da run corrente (transient — não persiste estritamente, mas hidrata pra runs interrompidas). */
  playerHp: number;
  playerMaxHp: number;
  actionClock: CombatActionClock;
  dodgeVector: { x: number; y: number };
  /** Inimigos transientes (não persistem entre sessões). */
  enemies: EnemyRuntime[];
  projectiles: CombatProjectile[];
  /** Picks oferecidos no momento (modal aberto se array.length > 0). */
  pendingPicks: MutationDef[];
}

export interface CombatSliceActions {
  setActiveRun: (run: ExpeditionRun | null) => void;
  incrementTotalRuns: () => void;
  setInExpedition: (b: boolean) => void;
  setPlayerHp: (hp: number) => void;
  setPlayerMaxHp: (mhp: number) => void;
  setActionClock: (clock: CombatActionClock) => void;
  setDodgeVector: (vector: { x: number; y: number }) => void;
  resetCombatRuntime: () => void;
  setEnemies: (e: EnemyRuntime[]) => void;
  updateEnemy: (rid: number, patch: Partial<EnemyRuntime>) => void;
  removeEnemy: (rid: number) => void;
  setProjectiles: (projectiles: CombatProjectile[]) => void;
  updateProjectile: (id: string, patch: Partial<CombatProjectile>) => void;
  removeProjectile: (id: string) => void;
  setPendingPicks: (defs: MutationDef[]) => void;
  clearPendingPicks: () => void;
}

export type CombatSlice = CombatSliceState & CombatSliceActions;

export const createCombatSlice: StateCreator<AppStore, [], [], CombatSlice> = (set) => ({
  activeRun: null,
  totalRuns: 0,
  inExpedition: false,
  playerHp: 100,
  playerMaxHp: 100,
  actionClock: makeCombatActionClock(),
  dodgeVector: { x: 0, y: 0 },
  enemies: [],
  projectiles: [],
  pendingPicks: [],
  setActiveRun: (run) => set({ activeRun: run }),
  incrementTotalRuns: () => set((s) => ({ totalRuns: s.totalRuns + 1 })),
  setInExpedition: (b) => set({ inExpedition: b }),
  setPlayerHp: (hp) => set({ playerHp: hp }),
  setPlayerMaxHp: (mhp) => set({ playerMaxHp: mhp }),
  setActionClock: (actionClock) => set({ actionClock }),
  setDodgeVector: (dodgeVector) => set({ dodgeVector }),
  resetCombatRuntime: () => set({
    actionClock: makeCombatActionClock(),
    dodgeVector: { x: 0, y: 0 },
    enemies: [],
    projectiles: [],
    pendingPicks: [],
  }),
  setEnemies: (enemies) => set({ enemies }),
  updateEnemy: (rid, patch) =>
    set((s) => ({
      enemies: s.enemies.map((e) => (e.rid === rid ? { ...e, ...patch } as EnemyRuntime : e)),
    })),
  removeEnemy: (rid) => set((s) => ({ enemies: s.enemies.filter((e) => e.rid !== rid) })),
  setProjectiles: (projectiles) => set({ projectiles }),
  updateProjectile: (id, patch) => set((s) => ({
    projectiles: s.projectiles.map((projectile) =>
      projectile.id === id ? { ...projectile, ...patch } : projectile),
  })),
  removeProjectile: (id) => set((s) => ({
    projectiles: s.projectiles.filter((projectile) => projectile.id !== id),
  })),
  setPendingPicks: (defs) => set({ pendingPicks: defs }),
  clearPendingPicks: () => set({ pendingPicks: [] }),
});
